import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedStyle, } from 'react-native-reanimated';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import UpgradeCard from 'design-system/UpgradeCard';
import IconPieChart from 'design-system/icons/IconPieChart';
import NotificationBanner from 'design-system/NotificationBanner';
import { CardView } from 'design-system/CardView';
import normalize from 'common/Normalize';
import { useHeaderHeight } from 'hooks';
import { isDefaultCategory } from 'hooks/useCategory';
import useColors from 'hooks/useColors';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import BudgetingGraphLabel from 'features/budgeting/components/BudgetingGraphLabel';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { formatPeriod } from 'utils/formatting';
import { selectDefaultCurrency } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { fetchCategoriesMerchants } from 'actions';
import checkFeatureFlag from 'utils/featureFlags';
import { selectCategoryTotals } from '../../selectors';
import CategoriesHistogram from './CategoriesHistogram';
import SubCategoriesList from './SubCategoriesList';
const CategoryTopContainer = ({ scrollY, categoryId, merchantId, step, initialPositionDate, defaultAmount, defaultFrom, defaultTo, onSelectedPeriod, categoryColor, isSubcategory, }) => {
    const colors = useColors();
    // We don’t pass this down as we don’t want this component recreating each time by the list header changing
    const categoryTotals = useAppSelector((store) => selectCategoryTotals(store, categoryId ?? merchantId));
    const navigation = useMainStackNavigation();
    const lineGraph = useMemo(() => {
        if (categoryTotals?.graph) {
            return categoryTotals.graph;
        }
        return undefined;
    }, [categoryTotals?.graph]);
    const dispatch = useAppDispatch();
    const onMonthSelected = useCallback((data) => {
        const { from, to } = categoryTotals?.graph?.[data] || {};
        if (!from || !to)
            return;
        navigation.setParams({ from, to });
        dispatch(fetchCategoriesMerchants(data));
        onSelectedPeriod({
            selectedPeriodStartDate: from,
            selectedPeriodEndDate: to,
            index: data,
        });
    }, [categoryTotals?.graph, onSelectedPeriod]);
    const currency = useAppSelector(selectDefaultCurrency);
    const [state, setState] = useState(() => {
        const selectedIndex = categoryTotals?.graph?.findIndex((e) => e.from === initialPositionDate) ??
            0;
        return {
            titleCurrency: currency,
            titleAmount: defaultAmount ?? 0,
            subTitle: defaultFrom && defaultTo
                ? formatPeriod({ from: defaultFrom, to: defaultTo })
                : '',
            selectedIndex: selectedIndex > -1 ? selectedIndex : 0,
        };
    });
    const selected = useCallback((data, isSettingTitle) => {
        const graph = categoryTotals?.graph;
        let from = null;
        let to = null;
        let currency = null;
        let value = 0;
        if (graph && graph[data] && graph[data].from) {
            from = graph[data].from;
            to = graph[data].to;
            currency = graph[data].currency;
            value = graph[data].value;
        }
        const subTitle = from && to ? formatPeriod({ from, to }) : '';
        setState((prev) => ({
            ...prev,
            subTitle,
            titleAmount: value,
            titleCurrency: currency || 'GBP',
            selectedIndex: data,
        }));
        if (!isSettingTitle)
            onMonthSelected(data);
    }, [categoryTotals?.graph, onMonthSelected]);
    useEffect(() => {
        if (categoryTotals?.graph && !categoryTotals.isFetching) {
            const index = categoryTotals.graph.findIndex((e) => e.from === initialPositionDate) ??
                0;
            selected(index > -1 ? index : 0, true);
        }
    }, [
        categoryTotals?.graph,
        categoryTotals?.isFetching,
        initialPositionDate,
        selected,
    ]);
    const { titleAmount, titleCurrency } = state;
    const headerHeight = useHeaderHeight();
    const imageOpacity = useAnimatedStyle(() => ({
        opacity: interpolate(scrollY.value, [0, headerHeight * 2], [1, 0.1], Extrapolation.CLAMP),
    }), [headerHeight]);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'customCategories',
    });
    const { isUnlockedForSpace: isSearchUnlockedForSpace, onPressUpgrade: onPressUpgradeForSearch, } = useShouldShowUpgrade({
        benefitId: 'search',
    });
    const showSubcategoriesCreateFlow = useAppSelector((store) => checkFeatureFlag(store.utils.featureFlags, 'show_subcategories_create_flow'));
    if (categoryId !== undefined &&
        !isUnlockedForSpace &&
        !isDefaultCategory(categoryId)) {
        return (<View>
        <Spacer.H16 />
        <UpgradeCard title="See your spending" description="Upgrade your plan to continue tracking your custom categories" icon={<IconPieChart />} onPressUpgrade={onPressUpgrade}/>
      </View>);
    }
    const valueOnly = step === 'custom' || merchantId === -1;
    const blurValue = !isSearchUnlockedForSpace && state.selectedIndex >= 2;
    return (<View>
      {valueOnly ? (<Spacer.H16 />) : (<CardView style={styles.histogramCard}>
          <Animated.View style={[styles.histogramContainer, imageOpacity]}>
            {lineGraph ? (<CategoriesHistogram lineGraph={lineGraph} categoryMerchantSelected={categoryId || merchantId || null} color={categoryColor} selectedIndex={state.selectedIndex} onMonthSelected={selected} currency={titleCurrency}/>) : null}
          </Animated.View>
        </CardView>)}
      {blurValue ? (<View>
          <NotificationBanner title="Unlock enhanced tracking" description="Enhanced tracking is available across all our premium plans. Get started today by learning more." positiveButtonTitle="Learn more" onPositiveBtnPress={onPressUpgradeForSearch}/>
        </View>) : null}
      <CardView style={[styles.valueContainer, styles.custom]} noMarginTop noMarginBottom>
        <BudgetingGraphLabel title={categoryTotals?.graph &&
            categoryTotals?.graph[state.selectedIndex] &&
            categoryTotals?.graph[state.selectedIndex].value > 0
            ? 'Income'
            : 'Spending'} color={categoryColor} amount={{
            amount: titleAmount,
            currency: titleCurrency,
        }} style={styles.graphLabel} amountProps={{
            color: titleAmount > 0 ? colors.text.income : colors.text.primary,
            showSign: true,
        }}/>
      </CardView>
      {!!categoryId && !isSubcategory && !!showSubcategoriesCreateFlow && (<SubCategoriesList categoryId={categoryId} selectedIndex={state.selectedIndex}/>)}
    </View>);
};
const styles = StyleSheet.create({
    histogramContainer: {
        height: normalize(154),
        marginVertical: rem(24),
    },
    histogramCard: {
        flex: 1,
        overflow: 'visible',
    },
    valueContainer: {
        flexDirection: 'row',
        marginHorizontal: rem(16),
    },
    graphLabel: {
        width: '100%',
    },
    custom: {
        paddingHorizontal: rem(16),
        paddingVertical: rem(8),
    },
});
export default memo(CategoryTopContainer);
